/* eslint-disable no-param-reassign */
import { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import { Box, CircularProgress } from '@mui/material';
import { each, isEmpty, isNull, toString } from 'lodash';
import { useParams } from 'react-router-dom';
import uuid from 'react-uuid';
import {
  ConvertibleNoteModel,
  DividendRateLedgerItemModel,
  LiqPrefLedgerItem,
  ProFormaCapTableModel,
  SecurityRead,
  SheetAttributeMapping,
} from 'api';
import { captableAction } from 'common/actions';
import { SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_COLLAPSE } from 'common/actions/row-groups/types';
import {
  ASSIGNED_TYPE_FOR_NOTES,
  CAP_TABLE_LOCKED_MESSAGE,
  CURRENT_OWNERSHIP_ROW_NUMBER,
  FIRM_LIQUIDATION_PREFERENCE_ALIAS,
  FIRM_OWNERSHIP_PERCENTAGE_ALIAS,
  FIRM_SHARES_ALIAS,
  FULLY_DILUTED_OWNERSHIP_ROW_NUMBER,
  INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER,
  LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
  MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER,
  PREFERRED_TERMS_ALIAS,
  RECALCULATE_TRIGGERING_ALIASES,
  SECURITY_TYPE_ROW_NUMBER,
  SHARES_FULLY_DILUTED_ALIAS,
  SHARES_FULLY_DILUTED_ROW_NUMBER,
  SHARES_OUTSTANDING_ALIAS,
  SHARES_OUTSTANDING_ROW_NUMBER,
  STRIKE_PRICE_ALIAS,
  TWO_DECIMAL_PLACES,
} from 'common/constants/cap-table';
import { RELATED_ENTITY } from 'common/constants/companies';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { DEBOUNCE_DELAY } from 'common/constants/general';
import { CAPTABLE_VERSION_FILTER } from 'common/constants/pageFilters';
import { OPTION, PREFERRED_STOCK, UNISSUED_OPTIONS, WARRANT } from 'common/constants/securityTypes';
import { type CompanyPageActionHandler, useCurrencySelector, useFormat } from 'common/hooks';
import { SystemExpandReason } from 'common/reducers/rowsGroups/types';
import { useStore } from 'common/store';
import { CapTableInfo } from 'common/types/capTable';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import { UseWorkbookValues } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, EmptyTableMessage } from 'components';
import { FinalAllocation } from 'components/AllocationFinalMessage/AllocationFinalMessage.props';
import { ExtendedFabButton } from 'components/Buttons';
import CurrencyMenu from 'components/CurrencyMenu';
import { ConfirmationCannotDelete, ConfirmationDeleteWithList, ConfirmationDialog } from 'components/Dialogs';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import { invokeNavigationSave } from 'components/ui/navigationActionFeedback';
import CapTableContext, { CapTableContextType } from 'context/CapTableContext';
import DialogContext from 'context/DialogContext';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { useLayoutContext } from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { useZoom } from 'context/ZoomContext';
import { useExportSignal } from 'features/pdfExport';
import { CellValue, ICell, IScalarSpreadsheetConfig } from 'models/interfaces/spreadsheet-types/core-types';
import { ConfirmationSaveFinal, ProformaCaptableDialog } from 'pages/CapTable/cap-table/components/index';
import { NewRoundModelerData } from 'pages/CapTable/cap-table/components/proforma-captable-dialog/NewRoundModeler.props';
import { blankPreferred, securityTemplate } from 'pages/CapTable/cap-table/data';
import {
  calculateColumnsCurrentOwnPercent,
  clearUnderlyingSecurity,
  linkedCellsUpdates,
  reverseParser,
} from 'pages/CapTable/cap-table/utilities';
import getOptionName, { getOptionLedgerSecurityName } from 'pages/CapTable/cap-table/utilities/getOptionName';
import { syncGroupedSecurityUnderlying } from 'pages/CapTable/cap-table/utilities/syncGroupedSecurityUnderlying';
import useCapTableTertiaryNavItems from 'pages/CapTable/useCapTableTertiaryNavItems';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useKeyEventForFabButton, useReadFieldAttributes, useResponse } from 'services/hooks';
import { useUpdateCapTableAndSecuritiesInfo } from 'services/hooks/captable';
import {
  CAPTABLE_MODEL_NAME,
  CAPTABLE_PAGE,
  CONVERTIBLE_NOTE_MODEL_NAME,
  DIV_RATE_LEDGER_ITEM_MODEL_NAME,
  SECURITY_MODEL_NAME,
} from 'services/hooks/useReadFieldAttributes/constants';
import { useCapTableDirtyCellStore } from 'store/useCapTableDirtyCellStore';
import { useReviewStore } from 'store/useReviewStore';
import { getBooleanValue } from 'utilities';
import { getLastColumn } from 'utilities/alphabet-utilities';
import { useAttributeMapperStore } from '../../../../../store/useAttributeMapperStore';
import convertCamelCaseToSnakeCase from '../../../../../utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import useCaptableData from '../../utilities/useCaptableData';
import { useReasonedRowGroupExpands } from '../../utilities/useReasonedRowGroupExpands';
import AISuggestionsColumnBanner from '../ai-suggestions-column-banner/AISuggestionsColumnBanner';
import CapTableWidgetDrawer from '../cap-table-widget-drawer/CapTableWidgetDrawer';
import { OptionsLedgerCell } from '../options-ledger/types';
import { ReviewCellOutline } from '../review-side-panel';
import { AIFieldAcceptProvider } from './AIFieldAcceptContext';
import {
  CapTableCell,
  CapTableCellWithReferences,
  CaptableColumn,
  CaptableTableProps,
  SourceNote,
} from './CaptableTable.props';
import CitationProvider from './CitationContext';
import {
  recalculateSecuritiesConstants,
  useAfterProformaCreate,
  useProFormaPermissions,
  useRecalculateSecurities,
} from './hooks';

const CapTableTable = ({
  measurementDate,
  selectedMeasurementDate,
  saveAsNewProforma,
  isCreatingProforma,
  filesToSave,
  addReferenceForExistingDocuments,
  notes,
  saveNotes,
  saveSpreadsheetNotes,
  spreadsheetNotesHasChanged,
  spreadsheetNotes,
  notesHasChanged,
  setIsDisplayingRowNumbers,
  setDocumentDisabledStatus,
  onAddRelatedEntity,
  onAddSecurityActionChange,
  onEditCaptable,
  onSaveAsNewProformaActionChange,
  widgetDrawerProps,
}: CaptableTableProps) => {
  const { zoomLevel } = useZoom();
  const [{ captableInfo, isShowLoadingProgress, companyInfo, fundList, firmInfo }] =
    useStore() as unknown as UseStoreValues;
  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: `${DEFAULT_CURRENCY} ${DEFAULT_CURRENCY_SYMBOL}`,
  });
  const {
    isCurrencyLoading,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    currencyDisplayLabel,
    currencyTooltipTitle,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  } = useCurrencySelector({ format, dispatch: formatDispatch });
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [captableId, setCaptableId] = useState<number>();
  const [finalAllocations, setFinalAllocations] = useState<FinalAllocation[]>([]);
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>();
  const [openProformaDialog, setOpenProformaDialog] = useState(false);
  const [nrmState, setNrmState] = useState<NewRoundModelerData | null>(null);
  const [persistentDeletedColumns, setPersistentDeletedColumns] = useState<CaptableColumn[]>([]);
  const { showDialog } = useContext(DialogContext);
  const [, updateCapTableData] = useUpdateCapTableAndSecuritiesInfo() as [
    data: CapTableInfo,
    updateCapTableData: (
      id: number,
      columns: CaptableColumn[],
      updatedTableData: CapTableInfo,
      updateGlobalStore: boolean
    ) => Promise<void>,
  ];
  const { buildColumnsForProforma, afterCreate } = useAfterProformaCreate({ nrmState, companyInfo });
  const fieldAttributes = useReadFieldAttributes(CAPTABLE_PAGE);
  const securityFieldAttrs = fieldAttributes.get(SECURITY_MODEL_NAME);
  const capTableFieldAttrs = fieldAttributes.get(CAPTABLE_MODEL_NAME);
  const convertibleNotesFieldAttrs = fieldAttributes.get(CONVERTIBLE_NOTE_MODEL_NAME);
  const divRateLedgerItemAttrs = fieldAttributes.get(DIV_RATE_LEDGER_ITEM_MODEL_NAME);
  const [hasVariableLiquidationPreferenceLedger, setHasVariableLiquidationPreferenceLedger] = useState(
    !!captableInfo?.has_liquidation_preference_ledger
  );
  const [hasVariableDividendRate, setHasVariableDividendRate] = useState(
    getBooleanValue(captableInfo?.has_dividend_rate_ledger)
  );
  const { getAttributeMapperManager } = useAttributeMapperStore();

  useEffect(() => {
    setHasVariableLiquidationPreferenceLedger(!!captableInfo?.has_liquidation_preference_ledger);
    setHasVariableDividendRate(getBooleanValue(captableInfo?.has_dividend_rate_ledger));
  }, [captableInfo]);

  const existingHiddenNoteColumnsMapRef = useRef<Record<number, number> | null>(null);
  // Non-cell column properties (e.g. multiple_investments) that reverseParser can't restore from cells
  const recalcOverridesRef = useRef<Map<string, Pick<CaptableColumn, 'multiple_investments'>>>(new Map());
  // Monotonic counter to detect stale recalculation responses after interleaving edits
  const editGenRef = useRef(0);
  const captableContainerRef = useRef<HTMLDivElement>(null);
  // Last fully-ready grid element, keyed by cap table id, so in-place workbook
  // rebuilds can keep the grid mounted instead of flashing the skeleton.
  const lastReadyGridRef = useRef<{ id: number; element: ReactElement } | null>(null);

  const { pageFilters, viewOnlyUser, openPdfDialog, currentFirmRole, isExportMode } = useLayoutContext();

  const savesAsProformaConfirmation = useCallback(() => {
    setOpenProformaDialog(true);
  }, []);

  const initialFormValues = useMemo(() => {
    const defaultProformaName = `${captableInfo?.name} Proforma`;

    return {
      isValid: true,
      values: {
        name: defaultProformaName,
        access_level: 'restricted' as const,
      },
      errors: {},
    };
  }, [captableInfo]);
  const [formState, setFormState] = useState(initialFormValues);
  const [showEmptyTableMessage, setShowEmptyTableMessage] = useState(false);
  const [updatedColumns, setUpdatedColumns] = useState<CaptableColumn[]>([]);

  const getExistingHiddenNoteColumns = (securities: SecurityRead[]) =>
    securities?.filter(s => s.is_hidden && ![WARRANT, OPTION].includes(+s.security_type!)) || [];

  const getExistingConvertibleNotes = (convertibleNotesFromColumns: CaptableColumn[]) =>
    convertibleNotesFromColumns.flatMap(column => column.convertible_notes).filter(note => note.id !== 0);

  // columns with notes
  const columnsWithNotes = useMemo(() => {
    if (updatedColumns?.length) {
      return updatedColumns.filter(column => !isEmpty(column.convertible_notes));
    }
    return [];
  }, [updatedColumns]);

  const existingConvertibleNotes = useMemo(() => getExistingConvertibleNotes(columnsWithNotes), [columnsWithNotes]);

  const existingHiddenNoteColumns = useMemo(
    () => getExistingHiddenNoteColumns(captableInfo?.securities ?? []),
    [captableInfo]
  );

  // When CapTable Version is changed, reset the hidden note columns map
  useEffect(() => {
    existingHiddenNoteColumnsMapRef.current = null;
  }, [captableId]);

  const existingHiddenNoteColumnsMap = useMemo((): Record<number, number> => {
    if (
      existingHiddenNoteColumnsMapRef.current === null &&
      !isEmpty(existingConvertibleNotes) &&
      !isEmpty(existingHiddenNoteColumns)
    ) {
      const hiddenNoteColumnsMap = existingHiddenNoteColumns.reduce((map, column) => {
        const parts = column.name.split(' - ');
        const baseSecurityName = parts[0];
        const noteName = parts[1];
        const foundKey = existingConvertibleNotes.find(note => {
          if (note.note_name !== noteName) {
            return false;
          }
          const parentCol = note.convertible_note_security
            ? columnsWithNotes.find(col => col.id === note.convertible_note_security)
            : undefined;
          // If we found the parent by ID, verify its name; otherwise fall back to name-only match
          if (parentCol) {
            return parentCol.name === baseSecurityName;
          }
          return columnsWithNotes.some(col => col.name === baseSecurityName);
        })?.id;
        if (foundKey) {
          // 'Conv Note - Investor Name' -> 'Investor Name'
          const tmpMap: any = { ...map };
          tmpMap[foundKey] = column.id;
          return tmpMap;
        }
        return map;
      }, {});
      existingHiddenNoteColumnsMapRef.current = hiddenNoteColumnsMap;
    }
    return existingHiddenNoteColumnsMapRef.current as Record<number, number>;
  }, [existingConvertibleNotes, existingHiddenNoteColumns, columnsWithNotes]);

  const {
    spreadsheet: captableSheet,
    tableTerms,
    visibleColumns,
    setVisibleColumns,
    virtualColumns,
    setColumns,
    columns,
    isButtonDisabled,
    setIsButtonDisabled,
    setCaptableSetProgrammaticUpdate,
    rowGroups,
    setRowGroups,
    ...restCaptableProps
  } = useCaptableData({
    captableInfo: captableInfo as CapTableInfo,
    fundList,
    existingHiddenNoteColumns,
    measurementDate,
    fieldAttributes: securityFieldAttrs,
    isDisabled,
    hasVariableLiquidationPreference: hasVariableLiquidationPreferenceLedger,
    hasVariableDividendRate,
    firmName: firmInfo?.name || '',
    // Pre-FX default source currency. Must match the FOD/CFL ledgers, which
    // read companyInfo.captable_currency - format.currency.code resolves to a
    // USD default and would mis-source the OIP Pre-FX annotation.
    captableCurrency: companyInfo?.captable_currency,
    // Drop the fund-ownership rows from the cap-table PDF capture; they are
    // exported on the dedicated Fund Ownership page instead.
    isExportMode,
  });

  const { errorNotification, infoNotification } = useResponse();

  const { recalculate, validateRecalculationInput } = useRecalculateSecurities({
    capTableId: captableInfo?.id ? Number(captableInfo.id) : undefined,
    measurementDate,
  });

  const hasPendingAINameFindings = useMemo(() => {
    const list =
      columns?.length > 0
        ? columns.filter(c => !c.is_deleted && !c.is_hidden)
        : ((captableInfo?.securities ?? []) as unknown as CaptableColumn[]);
    return list.some(security => {
      const meta = (security as any)?.ai_finding?.field_metadata?.name;
      return meta && !meta.is_accepted && meta.value !== undefined;
    });
  }, [columns, captableInfo?.securities]);

  // SCAL-9155: the in-grid AI name-suggestion banner (per-column accept/dismiss pills)
  // is gated behind a default-OFF toggle. The banner self-inerts when off; this drives
  // the matching top-padding reservation so no empty gap is left above the grid.
  const showAiNameBanner = useReviewStore(s => s.showAiColumnControls) && hasPendingAINameFindings;

  useEffect(() => {
    if (!isEmpty(captableInfo) && pageFilters?.length) {
      const capTableFilterId = CAPTABLE_VERSION_FILTER;
      const { selectedValue } = pageFilters.find(filter => toString(filter.id) === toString(capTableFilterId)) || {};

      setIsButtonDisabled(`${selectedValue}` === '');
    }
  }, [captableInfo, pageFilters, setIsButtonDisabled]);

  const spreadsheets = useMemo(() => [captableSheet], [captableSheet]) as unknown as IScalarSpreadsheetConfig[];

  const spreadsheetsRef = useRef(spreadsheets);
  spreadsheetsRef.current = spreadsheets;

  const {
    onChange: onCellsChanged,
    cells,
    data,
    evaluateSpreadsheets,
    workbook,
    areCellsValid,
    workbookContextValue,
  } = useWorkbook(spreadsheets) as unknown as UseWorkbookValues;

  const { cleanAction } = useContext(UnsavedChangesContext);

  const applyMultipleInvestmentsRecalculation = useCallback(
    (baseColumns: CaptableColumn[], recalculatedSecurity: CaptableColumn, columnLegend: string): CaptableColumn[] => {
      const multipleInvestments = recalculatedSecurity.multiple_investments;

      recalcOverridesRef.current.set(recalculatedSecurity.security_ref!, {
        multiple_investments: multipleInvestments,
      });

      let nextColumns = baseColumns;
      const ledgerCellKey = `${columnLegend}${MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER}`;
      const ledgerCell = cells?.captable?.[ledgerCellKey];

      if (ledgerCell) {
        nextColumns = onCellsChanged(
          ledgerCell as ICell,
          multipleInvestments as unknown as string,
          () => {}
        )[0] as CaptableColumn[];
      }

      return nextColumns.map(column => {
        if (column.security_ref === recalculatedSecurity.security_ref) {
          return { ...column, multiple_investments: multipleInvestments };
        }
        return column;
      });
    },
    [cells, onCellsChanged]
  );

  const onChange = useCallback(
    async (cell: CapTableCell, expr: string, customFn: () => void) => {
      const thisEdit = ++editGenRef.current;
      const reversedColumns = onCellsChanged(cell as ICell, expr, customFn)[0] as CaptableColumn[];

      if (visibleColumns?.length && cells) {
        setUpdatedColumns([...reversedColumns]);
      }
      if (cell.alias === LIQUIDATION_PREFERENCE_LEDGER_ALIAS) {
        // this will call parser and update the expressions for the accrued dividends and initial liquidation preference cells
        await evaluateSpreadsheets(spreadsheets);
      }

      // Trigger server-side recalculation of accrued dividends for preferred
      // securities with multiple investment dates when a dividend-affecting
      // field changes.
      if (cell.alias && RECALCULATE_TRIGGERING_ALIASES.has(cell.alias) && captableInfo?.id) {
        const editedColumn = reversedColumns.find(col => col.security_ref === cell.columnRef);
        if (
          editedColumn &&
          validateRecalculationInput({
            column: editedColumn,
            recalculationType: recalculateSecuritiesConstants.MULTIPLE_INVESTMENTS_TYPE,
          })
        ) {
          const recalculatedColumn = await recalculate([editedColumn]);
          if (recalculatedColumn) {
            // Apply recalculation only when this edit is still latest.
            if (editGenRef.current === thisEdit) {
              const updatedColumnsWithRecalculation = applyMultipleInvestmentsRecalculation(
                reversedColumns,
                recalculatedColumn[0],
                cell.columnLegend as string
              );
              setUpdatedColumns([...updatedColumnsWithRecalculation]);
            }
          }
        }
      }
    },
    [
      onCellsChanged,
      visibleColumns,
      cells,
      evaluateSpreadsheets,
      spreadsheets,
      captableInfo,
      applyMultipleInvestmentsRecalculation,
      recalculate,
      validateRecalculationInput,
    ]
  );

  // Visible columns based on the last spreadsheet changes
  useEffect(() => {
    // Reverse-parse only when the mounted sheet was built from the current
    // visibleColumns array (SpreadsheetConfig keeps that array by reference).
    // During an in-place rebuild the previous sheet's cells lag behind
    // visibleColumns, and reverseParser mutates the column objects (shared with
    // the store securities), so running it against stale cells would write old
    // values back over freshly patched ones. SCAL-8959.
    if (visibleColumns?.length && captableSheet?.cells && captableSheet.columns === visibleColumns) {
      const reversedColumns = reverseParser({
        cells: captableSheet.cells,
        columns: visibleColumns,
        fieldAttributes: securityFieldAttrs,
        allowEmptyValues: false,
      });

      // Re-apply non-cell properties that reverseParser can't restore from grid cells
      recalcOverridesRef.current.forEach((override, ref) => {
        const col = reversedColumns.find(c => c.security_ref === ref);
        if (col) {
          Object.assign(col, override);
        }
      });

      const hasChanged = JSON.stringify(reversedColumns) !== JSON.stringify(updatedColumns);
      if (hasChanged) {
        setUpdatedColumns([...reversedColumns]);
      }
    } else if (updatedColumns.length > 0) {
      // Only clear if there's actually something to clear
      setUpdatedColumns([]);
    }
  }, [captableSheet, cells, securityFieldAttrs, visibleColumns, updatedColumns]);

  // Debounced effect to show EmptyTableMessage only after a delay
  useEffect(() => {
    const shouldShowEmptyMessage = isEmpty(visibleColumns) && captableSheet?.tableData?.id;

    if (shouldShowEmptyMessage) {
      const timeout = setTimeout(() => {
        setShowEmptyTableMessage(true);
      }, DEBOUNCE_DELAY);

      return () => clearTimeout(timeout);
    }

    setShowEmptyTableMessage(false);
    return undefined;
  }, [visibleColumns, captableSheet?.tableData?.id]);

  const deletedColumns = useMemo(() => {
    if (columns?.length && cells?.captable) {
      const auxColumns = columns.filter(column => column.is_deleted && column.id !== 0);
      return auxColumns;
    }
    return [];
  }, [columns, cells]);

  // here are visible columns with ledger.
  const columnsWithLedgers = useMemo(() => {
    if (updatedColumns?.length) {
      return updatedColumns.filter(uc => uc.options_ledger?.length > 1);
    }
    return [];
  }, [updatedColumns]);

  const getTotalValue = useCallback(
    (rowNumber: number) => {
      const totalColumnLegend = getLastColumn(1);
      const totalCell = cells?.captable?.[`TOTALS_${totalColumnLegend + rowNumber}`];
      if (totalCell?.value) {
        const alias = totalCell.alias ?? '';
        const shareAliases = [SHARES_OUTSTANDING_ALIAS, SHARES_FULLY_DILUTED_ALIAS];
        let decimalPlaces = TWO_DECIMAL_PLACES;
        if (shareAliases.includes(alias)) {
          decimalPlaces = 1;
        } else if (capTableFieldAttrs && alias) {
          decimalPlaces = capTableFieldAttrs[alias]?.decimal_places;
        }
        return Number(totalCell.value).toFixed(decimalPlaces);
      }
      return '0';
    },
    [capTableFieldAttrs, cells]
  );

  const calculateDilutedOwnPercent = useCallback(
    (rowShares: number) => (rowShares / Number(getTotalValue(SHARES_FULLY_DILUTED_ROW_NUMBER))).toFixed(15),
    [getTotalValue]
  );

  // The current ownership depends on the diluted shares with the exception of unissued options
  const calculateCurrentOwnPercent = useCallback(
    (rowShares: number) =>
      (
        rowShares /
        Number(
          visibleColumns?.reduce((acc, column) => {
            if (column.security_type !== UNISSUED_OPTIONS) {
              return acc + Number(column.shares_diluted);
            }
            return acc;
          }, 0)
        )
      ).toFixed(15),
    [visibleColumns]
  );

  const createHiddenSecurity = (
    column: SecurityRead,
    sourceNote: SourceNote,
    assignedSecurityType: number,
    baseContent: SecurityRead,
    baseName: string,
    baseSeniority: number,
    baseLiquidationPreference: number
  ) => ({
    ...baseContent,
    name: `${baseName} - ${sourceNote.note_name}`,
    is_deleted: sourceNote.is_deleted,
    conversion_rate: '1.00',
    investment_date: sourceNote.issuance_date,
    issue_price: sourceNote.conversion_price_per_share || 0,
    security_type: assignedSecurityType,
    current_own_percent: sourceNote.current_ownership || 0,
    security_group_ref: column.security_ref,
    shares_outstanding: sourceNote.expected_shares || 0,
    shares_diluted: sourceNote.expected_shares || 0,
    loan_value: sourceNote.loan_value,
    seniority: baseSeniority,
    liquidation_preference: baseLiquidationPreference,
  });

  const updatedHiddenNoteColumns = useMemo(() => {
    const hiddenSecurities: CaptableColumn[] = [];
    if (visibleColumns?.length && !isNull(captableInfo) && !isEmpty(existingHiddenNoteColumnsMap)) {
      if (existingConvertibleNotes?.length) {
        existingHiddenNoteColumns.forEach(column => {
          const tmpSourceNote =
            existingConvertibleNotes.find(
              note =>
                note.security_group_ref === column.security_ref || existingHiddenNoteColumnsMap[note.id!] === column.id
            ) || {};

          const sourceNote: Partial<SourceNote> = tmpSourceNote ? { ...tmpSourceNote } : {};
          const sourceColumn =
            columnsWithNotes.find(col => col.id === sourceNote.convertible_note_security) || ({} as CaptableColumn);
          const assignedSecurityType = ASSIGNED_TYPE_FOR_NOTES[sourceNote.model_as_equity!];
          const baseContent =
            assignedSecurityType === PREFERRED_STOCK
              ? column
              : ({ ...column, ...blankPreferred } as unknown as SecurityRead);
          const baseName = sourceColumn.name;
          const baseSeniority = assignedSecurityType === PREFERRED_STOCK ? sourceColumn.seniority : 0;
          const baseLiquidationPreference =
            assignedSecurityType === PREFERRED_STOCK ? sourceColumn.liquidation_preference : 0;

          const security = createHiddenSecurity(
            column,
            sourceNote as SourceNote,
            assignedSecurityType,
            baseContent,
            baseName,
            +baseSeniority!,
            +baseLiquidationPreference!
          );
          hiddenSecurities.push(security as unknown as CaptableColumn);
        });
      }
    }
    return hiddenSecurities;
  }, [
    captableInfo,
    columnsWithNotes,
    visibleColumns,
    existingConvertibleNotes,
    existingHiddenNoteColumns,
    existingHiddenNoteColumnsMap,
  ]);

  const updatedHiddenColumns = useMemo(() => {
    const hiddenSecurities: CaptableColumn[] = [];
    if (visibleColumns?.length) {
      const existingHiddenColumns =
        captableInfo?.securities?.filter(s => s.is_hidden && [WARRANT, OPTION].includes(s.security_type!)) || [];

      // Get ledger rows from virtual columns only (columns where id is null)
      const virtualOptionLedgersRows =
        columnsWithLedgers
          .filter(col => isNull(col.id))
          .flatMap(col => col.options_ledger)
          .filter((row): row is OptionsLedgerCell => !!row) || [];

      existingHiddenColumns.forEach(col => {
        const ledgerRowData = virtualOptionLedgersRows.find(row => row.security_ref === col.security_ref);

        const { shares, price } = ledgerRowData || {};
        const { security_group_ref: securityGroupRef, strike_price: strikePrice } = col;
        const parentColumn = columnsWithLedgers.find(c => c.security_ref === col.security_group_ref);
        const parentLedgerRows = parentColumn?.options_ledger ?? [];
        const ledgerRowIndex = parentLedgerRows.findIndex(
          (row: OptionsLedgerCell) => row.security_ref === col.security_ref
        );
        // Always use price from the ledger. Matching strikes receive stable suffixes so every hidden security stays unique.
        const name =
          ledgerRowIndex >= 0
            ? getOptionLedgerSecurityName(parentLedgerRows, ledgerRowIndex, parentColumn?.name ?? '')
            : getOptionName(price as string | number, parentColumn?.name ?? '');
        const security = {
          ...col,
          ...syncGroupedSecurityUnderlying(col, parentColumn),
          current_own_percent: Number(calculateCurrentOwnPercent(Number(shares))),
          diluted_own_percent: Number(calculateDilutedOwnPercent(Number(shares))),
          name,
          shares_outstanding: shares,
          strike_price: strikePrice,
          // A hidden column not in ledger rows means it was deleted from the ledger
          is_deleted: isEmpty(ledgerRowData),
          order: visibleColumns.find(vc => vc.security_group_ref === securityGroupRef)?.order,
        };
        hiddenSecurities.push(security as unknown as CaptableColumn);
      });
    }
    return hiddenSecurities;
  }, [visibleColumns, captableInfo, columnsWithLedgers, calculateCurrentOwnPercent, calculateDilutedOwnPercent]);

  // this memo represents a virtual column changing to a real column because of a security type change
  // options_ledger and other characteristics of the virtual column are removed
  const fromVirtualToReal = useMemo(() => {
    if (updatedColumns?.length) {
      const tmpCols = updatedColumns
        .filter(col => isNull(col.id) && ![WARRANT, OPTION].includes(col.security_type))
        .map(col => ({
          ...col,
          id: 0,
          security_group_ref: null,
          underlying_security_ref: null,
          options_ledger: [],
        }));
      return tmpCols;
    }
    return [];
  }, [updatedColumns]);

  useEffect(() => {
    if (updatedColumns) {
      updatedColumns.forEach((updatedCol, index, arr) => {
        const { id, options_ledger } = updatedCol;
        if (!id && options_ledger?.length === 1) {
          // Persist the virtual column as an actual security...
          // eslint-disable-next-line no-param-reassign
          arr[index].id = 0;
          // eslint-disable-next-line no-param-reassign
          arr[index].security_group_ref = null;
          // ...and make the security_ref of the ledger row null.
          // This will generate a new hidden security based on that one row.
          // eslint-disable-next-line no-param-reassign
          arr[index].options_ledger = [
            {
              ...options_ledger[0],
              security_ref: null,
            },
          ];
        }
      });
    }
  }, [updatedColumns]);

  // see if I can catch the ones I want
  const deletedBySecurityTypeChange = useMemo(() => {
    if (fromVirtualToReal?.length) {
      const aux = [...fromVirtualToReal];
      let deleted: CaptableColumn[] = [];
      aux.forEach(virtual => {
        const toDelete = columns
          ?.filter(col => col.security_group_ref === virtual.security_ref)
          .map(col => ({ ...col, is_deleted: true }));
        deleted = [...deleted, ...toDelete];
      });
      return deleted;
    }
    return [];
  }, [columns, fromVirtualToReal]);

  const getColumnIndex = (cols: CaptableColumn[], securityRef: string) => {
    const columnByRef = cols.find(column => column.security_ref === securityRef);
    return cols.indexOf(columnByRef as CaptableColumn);
  };

  const hiddenColumns = useMemo(() => {
    const securities: CaptableColumn[] = [];
    // columnsWithLedgers comes from a memo looking at every col with options_ledger.length > 1
    columnsWithLedgers.forEach(column => {
      const { options_ledger: optionsLedger } = column;
      if ((optionsLedger ?? []).length > 1) {
        const ledgerRows = optionsLedger ?? [];
        ledgerRows.forEach((row: OptionsLedgerCell, rowIndex: number) => {
          // Rows without security refs represent new hidden columns.
          if (row.security_ref) return;

          const name = getOptionLedgerSecurityName(ledgerRows, rowIndex, column.name);
          securities.push({
            ...column,
            // An options/warrant security with only one row in the ledger does not make up any virtual columns.
            // However, when more rows are added, all of them need to be converted into hidden securities.
            id: column.isVirtualColumn || ledgerRows.length >= 2 ? 0 : null,
            security_ref: uuid(),
            name,
            is_hidden: true,
            security_group_ref: column.security_ref,
            shares_diluted: row.shares.toString(),
            diluted_own_percent: Number(calculateDilutedOwnPercent(Number(row.shares))),
            shares_outstanding: row.shares.toString(),
            current_own_percent: Number(calculateCurrentOwnPercent(Number(row.shares))),
            strike_price: row.price.toString(),
            order: getColumnIndex(updatedColumns, column.security_ref!),
          });
        });
      }
    });

    return securities;
  }, [columnsWithLedgers, calculateDilutedOwnPercent, calculateCurrentOwnPercent, updatedColumns]);

  // hidden columns that come from notes in the CN ledger
  const fromNotesToSecurities = useMemo(() => {
    const securities: CaptableColumn[] = [];
    columnsWithNotes.forEach(column => {
      const { convertible_notes: convertibleNotes } = column;
      if ((convertibleNotes ?? []).length >= 1) {
        (column.convertible_notes ?? [])
          .filter(ledgerNote => ledgerNote.id === 0 && !ledgerNote.is_deleted)
          .forEach(note => {
            const assignedSecurityType = ASSIGNED_TYPE_FOR_NOTES[note.model_as_equity!];
            const baseContent = assignedSecurityType === PREFERRED_STOCK ? column : { ...column, ...blankPreferred };
            const security = {
              ...baseContent,
              id: 0,
              security_ref: note.security_group_ref,
              name: `${column.name} - ${note.note_name}`,
              is_hidden: true,
              investment_date: note.issuance_date,
              issue_price: note.conversion_price_per_share || 0,
              security_type: assignedSecurityType,
              security_group_ref: column.security_ref,
              shares_outstanding: note.expected_shares || 0,
              shares_diluted: note.expected_shares || 0,
              cap_table: captableInfo?.id,
              convertible_notes: [],
              diluted_own_percent: Number(calculateDilutedOwnPercent(Number(note.expected_shares ?? '0'))),
              current_own_percent: Number(calculateCurrentOwnPercent(Number(note.expected_shares ?? '0'))),
              total_note_value_at_md: note.value_at_md,
              total_note_preference: note.note_principle_amount,
            } as unknown as CaptableColumn;
            securities.push(security);
          });
      }
    });
    return securities;
  }, [columnsWithNotes, captableInfo, calculateDilutedOwnPercent, calculateCurrentOwnPercent]);

  const saveConfirmation = useCallback(
    (callback: () => Promise<void>) => {
      if (isAllocationFinal) {
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Confirmation',
          },
          content: ConfirmationSaveFinal,
          actions: [
            {
              label: 'Cancel',
              variant: 'text',
              type: 'primary',
            },
            {
              label: 'Save',
              variant: 'contained',
              type: 'success',
              callback,
            },
          ],
        });
      }
    },
    [isAllocationFinal, showDialog]
  );

  const updatedTableData = useMemo(() => {
    if (isEmpty(cells)) {
      return { ...captableInfo };
    }

    let tmpSecurities = isEmpty(captableInfo?.securities) ? updatedColumns : captableInfo?.securities;
    tmpSecurities = tmpSecurities ?? [];

    // Remove the variable config from the securities if the captable does not variable props configured
    tmpSecurities = tmpSecurities.map((security: SecurityRead | CaptableColumn) => ({
      ...security,
      ...(!hasVariableLiquidationPreferenceLedger && { liquidation_preference_ledger_config: null }),
      ...(!hasVariableDividendRate && { dividend_rate_ledger_items: [] }),
    })) as SecurityRead[];

    return {
      ...captableInfo,
      has_liquidation_preference_ledger: hasVariableLiquidationPreferenceLedger,
      has_dividend_rate_ledger: hasVariableDividendRate,
      securities: tmpSecurities?.filter(s => !s.is_hidden),
      shares_outstanding: getTotalValue(SHARES_OUTSTANDING_ROW_NUMBER),
      shares_diluted: getTotalValue(SHARES_FULLY_DILUTED_ROW_NUMBER),
      current_own_percent: Number(getTotalValue(CURRENT_OWNERSHIP_ROW_NUMBER)).toFixed(15),
      diluted_own_percent: Number(getTotalValue(FULLY_DILUTED_OWNERSHIP_ROW_NUMBER)).toFixed(15),
      initial_liquidation_preference: getTotalValue(INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER),
      access_level: formState.values.access_level ?? captableInfo?.access_level ?? 'restricted',
    };
  }, [
    cells,
    captableInfo,
    updatedColumns,
    getTotalValue,
    hasVariableLiquidationPreferenceLedger,
    hasVariableDividendRate,
    formState,
  ]);

  const generateNewSecurities = (count: number) => {
    const securityArray: SecurityRead[] = [];

    for (let i = 0; i < count; i++) {
      const securityRef = uuid();
      const newSecurity = {
        ...securityTemplate,
        security_ref: securityRef,
        columnRef: securityRef,
        cap_table: captableInfo!.id!,
      };
      securityArray.push(newSecurity as unknown as SecurityRead);
    }

    return securityArray;
  };

  const addSecurity = (newColumns = 1) => {
    if (!cells?.captable) {
      return;
    }

    const securities = generateNewSecurities(newColumns);

    const tmpPersistedColumns = reverseParser({
      cells: cells.captable as Record<string, CapTableCell>,
      columns: visibleColumns,
      fieldAttributes: securityFieldAttrs,
    });
    const visible = [...securities, ...tmpPersistedColumns] as CaptableColumn[];
    visible.forEach((column, index) => {
      column.order = index;
      if (!column.columnRef && column.security_ref) {
        column.columnRef = column.security_ref;
      }
    });
    const allColumns = [...visible, ...deletedColumns];
    setColumns(allColumns as CaptableColumn[]);
  };

  const addSecurityRef = useRef(addSecurity);
  addSecurityRef.current = addSecurity;
  const handleAddSecurity = useCallback(() => addSecurityRef.current(1), []);
  const hasCaptableCells = Boolean(cells?.captable);

  // Drag-reorder updates visible columns only. Write the new `order` back onto
  // the source column list so a later visible-column rebuild doesn't snap the
  // unsaved security back to its original position.
  const handleSpreadsheetColumnsChange = useCallback(
    (nextVisible: CaptableColumn[]) => {
      setVisibleColumns(nextVisible);
      const orderByRef = new Map<string, number>();
      nextVisible.forEach(col => {
        if (col.isVirtualColumn || !col.security_ref || col.order == null) {
          return;
        }
        orderByRef.set(col.security_ref, col.order);
      });
      if (orderByRef.size === 0) {
        return;
      }
      setColumns(
        columns.map(col => {
          if (!col.security_ref || !orderByRef.has(col.security_ref)) {
            return col;
          }
          const nextOrder = orderByRef.get(col.security_ref)!;
          const nextColumnRef = col.columnRef || col.security_ref;
          if (col.order === nextOrder && col.columnRef === nextColumnRef) {
            return col;
          }
          return { ...col, order: nextOrder, columnRef: nextColumnRef };
        }) as CaptableColumn[]
      );
    },
    [columns, setColumns, setVisibleColumns]
  );

  const deleteColumn = useCallback(
    // `serverDeleted` marks a grid-only mirror of a deletion the SERVER already
    // performed (AI-review auto-delete): the column hides immediately but is NOT
    // queued for the next save - re-sending is_deleted would re-delete a
    // security a review reopen may have restored in the meantime.
    (columnIndex: number, captableCells, options?: { cascade?: boolean; serverDeleted?: boolean }) => {
      const tmpPersistedColumns = [
        ...reverseParser({
          cells: captableCells,
          columns: visibleColumns,
          fieldAttributes: securityFieldAttrs,
        }),
      ];

      tmpPersistedColumns[columnIndex].is_deleted = true;
      if (options?.cascade) {
        tmpPersistedColumns[columnIndex].cascade_delete = true;
      }
      const relatedDeleted: CaptableColumn[] = [];
      // check if has notes, if it has, delete the related hidden columns
      if (tmpPersistedColumns[columnIndex].security_type === 6) {
        const relatedHiddenSecuritiesRef = (tmpPersistedColumns[columnIndex].convertible_notes ?? []).map(
          (note: ConvertibleNoteModel) => note.security_group_ref
        );

        captableInfo?.securities?.forEach(security => {
          if (relatedHiddenSecuritiesRef.includes(security.security_ref)) {
            relatedDeleted.push({
              ...security,
              is_deleted: true,
              ...(options?.cascade && { cascade_delete: true }),
            } as unknown as CaptableColumn);
          }
        });
      }

      if (!options?.serverDeleted) {
        setPersistentDeletedColumns([...persistentDeletedColumns, ...relatedDeleted, tmpPersistedColumns[columnIndex]]);
      }
      setColumns([...tmpPersistedColumns, ...deletedColumns]);
    },
    [captableInfo, deletedColumns, persistentDeletedColumns, securityFieldAttrs, setColumns, visibleColumns]
  );

  const handleDismissAiColumn = useCallback(
    // Grid mirror of a SERVER-side security deletion (the review decline
    // dismissed the last finding of an AI-created security). The server flag is
    // authoritative - no client-side finding inspection.
    (securityId: number) => {
      if (!visibleColumns || !captableSheet?.cells) {
        return;
      }
      const columnIndex = visibleColumns.findIndex(col => col.id === securityId);
      if (columnIndex < 0) {
        return;
      }
      deleteColumn(columnIndex, captableSheet.cells, { serverDeleted: true });
    },
    [visibleColumns, captableSheet, deleteColumn]
  );

  const sharedProps = {
    workbook,
    onChange,
    page: 'captable',
    setIsDisplayingRowNumbers,
  };

  const cloneColumn = useCallback(
    (columnIndex: number) => {
      const tmpPersistedColumns = [
        ...reverseParser({
          cells: captableSheet?.cells as Record<string, CapTableCell>,
          fieldAttributes: securityFieldAttrs,
          columns: visibleColumns,
        }),
      ];

      const column = tmpPersistedColumns[columnIndex];
      if (column) {
        const nameCount = tmpPersistedColumns.filter(s => s.name.toString().includes(`${column.name} copy`)).length + 1;

        const clonedOptions = (column.options_ledger ?? []).map((securityRow: OptionsLedgerCell) => ({
          ...securityRow,
          security_ref: undefined,
        }));

        const clonedNotes = (column.convertible_notes ?? []).map((note: ConvertibleNoteModel) => ({
          ...note,
          id: 0,
          convertible_note_security: undefined,
          security_group_ref: uuid(),
        }));

        const clonedDivRateLedgerItems = (column.dividend_rate_ledger_items ?? []).map(
          (item: DividendRateLedgerItemModel) => ({
            ...item,
            id: null,
            security: undefined,
          })
        );
        // ids for this needs to be undefined as the serializer works that way
        const clonedLiquidationPreferenceConfig = column.liquidation_preference_ledger_config
          ? {
            id: undefined,
            threshold_type: column.liquidation_preference_ledger_config.threshold_type,
            items: (column?.liquidation_preference_ledger_config?.items ?? []).map((item: LiqPrefLedgerItem) => ({
              ...item,
              id: undefined,
              ledger_config: undefined,
            })),
          }
          : null;

        const securityRef = uuid();
        const newSecurity = {
          ...column,
          id: 0,
          security_ref: securityRef,
          columnRef: securityRef,
          options_ledger: clonedOptions,
          convertible_notes: clonedNotes,
          dividend_rate_ledger_items: clonedDivRateLedgerItems,
          liquidation_preference_ledger_config: clonedLiquidationPreferenceConfig,
          is_deleted: false,
          name: `${column.name} copy ${nameCount > 1 ? nameCount : ''}`,
        };

        const allVisible = [newSecurity, ...tmpPersistedColumns] as CaptableColumn[];
        allVisible.forEach((col, index) => {
          col.order = index;
        });
        const allColumns = [...allVisible, ...deletedColumns];
        setColumns(allColumns as CaptableColumn[]);
      }
    },
    [captableSheet, deletedColumns, securityFieldAttrs, setColumns, visibleColumns]
  );

  const showDeleteConfirmation = useCallback(
    (columnIndex: number) => {
      if (!visibleColumns) {
        return;
      }
      const security = visibleColumns.find(column => column.order === columnIndex)!;
      const { backsolve_usage_details: backsolveUsageDetails, fod_usage_details: fodUsageDetails } = security;
      const {
        is_used_in_backsolve: isUsedInBacksolve,
        is_sole_basket_member: isSoleBasketMember,
        location: backsolveLocation,
      } = backsolveUsageDetails || {};
      const { is_used_in_fod: isUsedInFod, location: fodLocation } = fodUsageDetails || {};

      const messages: string[] = [];
      if (security.has_multiple_investments && security.multiple_investments?.length) {
        messages.push('All tranches in Multiple Investments Dates.');
      }

      if (isSoleBasketMember) {
        const soleBasketMessages = Array.isArray(backsolveLocation) ? [...backsolveLocation] : [];
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Action not allowed',
          },
          content: ConfirmationCannotDelete,
          contentProps: {
            messages: [
              ...soleBasketMessages,
              'It is the only security in its backsolve basket - replace it in the backsolve first.',
            ],
            itemName: 'security',
          },
          actions: [
            {
              label: 'Cancel',
              variant: 'contained',
              type: 'primary',
            },
          ],
        });
        return;
      }

      if (isUsedInFod || isUsedInBacksolve) {
        if (Array.isArray(fodLocation) && isUsedInFod) {
          messages.push(...fodLocation.map(location => `${location} - its fund ownership rows will be deleted`));
        }
        if (Array.isArray(backsolveLocation) && isUsedInBacksolve) {
          messages.push(...backsolveLocation.map(location => `${location} - removed from its backsolve basket`));
        }
        messages.push('Cash flow ledger rows for this security will also be deleted.');
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: `Delete ${security.name} everywhere?`,
          },
          content: ConfirmationDeleteWithList,
          contentProps: { messages, itemName: 'security' },
          actions: [
            {
              label: 'Delete everywhere',
              variant: 'contained',
              type: 'danger',
              callback: () => deleteColumn(columnIndex, captableSheet?.cells, { cascade: true }),
            },
          ],
        });
        return;
      }

      showDialog({
        wrapper: ConfirmationDialog,
        wrapperProps: {
          title: `Are you sure you want to permanently delete ${security.name}?`,
        },
        content: ConfirmationDeleteWithList,
        contentProps: { messages },
        actions: [
          {
            label: 'Delete',
            variant: 'contained',
            type: 'danger',
            callback: () => deleteColumn(columnIndex, captableSheet?.cells),
          },
        ],
      });
    },
    [visibleColumns, showDialog, deleteColumn, captableSheet]
  );

  const saveProformaColumns = useCallback(
    async activeColumns => {
      // Don't change isDisabled when creating a proforma from a primary cap table
      // Primary cap tables should remain read-only even if proforma creation fails
      // Only enable editing for non-primary cap tables if they were disabled
      if (isDisabled && !captableInfo?.is_primary) {
        setIsDisabled(false);
      }

      await saveAsNewProforma(
        {
          ...updatedTableData,
          name: formState.values.name ?? `${captableInfo?.name} Proforma`,
          securities: buildColumnsForProforma(activeColumns),
          is_primary: false,
        } as ProFormaCapTableModel,
        afterCreate
      );
    },
    [afterCreate, buildColumnsForProforma, captableInfo, formState, isDisabled, saveAsNewProforma, updatedTableData]
  );

  const saveCapTable = useCallback(
    async (updateGlobalStore = true, saveAsProForma = false) => {
      if (captableSheet?.columns !== visibleColumns) {
        // Sheet is mid-rebuild after an in-place store patch (SCAL-8959); a save now
        // would assemble a stale, destructive payload. The window is sub-second.
        infoNotification('The cap table is refreshing, please try saving again.');
        throw new Error('Cap table save skipped: sheet is refreshing');
      }

      if (!areCellsValid()) {
        // Strict false only: validateCells leaves untouched cells' isValid undefined.
        const invalidCells = Object.values(cells.captable).filter(cell => cell.isValid === false);

        invalidCells.forEach(cell => {
          if (cell.parent) {
            setRowGroups({ type: SYSTEM_EXPAND, row: cell.parent, reason: 'invalid-save' });
          }
        });
        errorNotification(
          invalidCells.length
            ? `Cap table was not saved: ${invalidCells.length} ${
                invalidCells.length === 1 ? 'cell has an invalid value' : 'cells have invalid values'
              }. Fix the highlighted cells and save again.`
            : 'Cap table was not saved: some values are invalid. Fix the highlighted cells and save again.'
        );
        throw new Error('Cap table save blocked by invalid cells');
      }

      // Save files in widget
      if (!isEmpty(filesToSave)) {
        addReferenceForExistingDocuments(
          Number(selectedMeasurementDate.id),
          filesToSave,
          Number(captableInfo?.id),
          CAP_TABLE_REFERENCE_TYPE
        );
      }

      if (!isEmpty(notes) && notesHasChanged) {
        saveNotes();
      }

      if (!isEmpty(spreadsheetNotes) && spreadsheetNotesHasChanged) {
        saveSpreadsheetNotes();
      }

      // Save table
      const deletedColumnsIds = new Set(deletedColumns.map(col => col.id));
      const originalLedgerColumns = columnsWithLedgers.map(column => ({
        ...column,
        is_deleted: true,
        // Undefined is used to force the column to be deleted in the database
        options_ledger_data_updated: isNull(column.id) || undefined,
      }));

      const updatedFilteredColumns =
        updatedColumns?.filter(
          updatedCol => !originalLedgerColumns.map(uc => uc.security_ref).includes(updatedCol.security_ref)
        ) || [];

      // This excludes columns that were added and deleted before saving them in the database
      const persistentDeletedFilteredColumns = persistentDeletedColumns.filter(
        col => col.id !== 0 && !deletedColumnsIds.has(col.id)
      );

      const persistentDeletedIds = new Set(persistentDeletedFilteredColumns.map(({ id }) => id));

      const hiddenNotesColumns = updatedHiddenNoteColumns.map(col => {
        const is_deleted = persistentDeletedIds.has(col.id) ? true : col.is_deleted;
        return {
          ...col,
          is_deleted,
        };
      });

      const deletedBySecurityChangeIds = new Set(deletedBySecurityTypeChange.map(({ id }) => id));

      // Create a Set of existing security_refs for deduplication
      const existingSecurityRefs = new Set(captableInfo?.securities?.map(s => s.security_ref).filter(Boolean) || []);

      // Deduplicate hiddenColumns - filter out any with existing security_ref
      const deduplicatedHiddenColumns = hiddenColumns.filter(
        col => col.security_ref && !existingSecurityRefs.has(col.security_ref)
      );

      let activeColumns = [
        ...updatedFilteredColumns,
        ...updatedHiddenColumns.filter(col => !deletedBySecurityChangeIds.has(col.id)),
        ...hiddenNotesColumns,
        ...deduplicatedHiddenColumns, // Use deduplicated version instead of hiddenColumns
        ...fromNotesToSecurities,
        ...fromVirtualToReal,
      ];

      activeColumns = calculateColumnsCurrentOwnPercent(activeColumns);

      if (saveAsProForma) {
        saveProformaColumns(activeColumns);
        return;
      }
      const shouldRemoveDivRateLedgerItems = (column: CaptableColumn) =>
        Boolean(!updatedTableData.has_dividend_rate_ledger || column.has_multiple_investments || !column.has_dividends);

      const attributeMapperManager = getAttributeMapperManager();
      const attributeMappingManagerData: SheetAttributeMapping = attributeMapperManager
        ? convertCamelCaseToSnakeCase(attributeMapperManager?.sheetAttributeMapping)
        : {};

      const shouldRemoveLiqPrefLedgerConfig = (column: CaptableColumn) =>
        Boolean(!updatedTableData.has_liquidation_preference_ledger || column.has_multiple_investments);
      const allColumns = [
        ...activeColumns,
        ...deletedColumns,
        ...deletedBySecurityTypeChange,
        ...persistentDeletedFilteredColumns,
        ...originalLedgerColumns,
      ].map(column => ({
        ...column,
        ...(shouldRemoveLiqPrefLedgerConfig(column) && {
          liquidation_preference_ledger_config: null,
        }),
        ...(shouldRemoveDivRateLedgerItems(column) && { dividend_rate_ledger_items: [] }),
        ...clearUnderlyingSecurity(column),
      })) as CaptableColumn[];

      const updatedTableDataWithAttributeMapping = {
        ...updatedTableData,
        sheet_attribute_mapping: attributeMappingManagerData,
      };

      if (isAllocationFinal) {
        saveConfirmation(async () => {
          await updateCapTableData(
            Number(captableInfo?.id),
            allColumns,
            updatedTableDataWithAttributeMapping,
            updateGlobalStore
          );
          onEditCaptable();
        });
      } else {
        await updateCapTableData(
          Number(captableInfo?.id),
          allColumns,
          updatedTableDataWithAttributeMapping,
          updateGlobalStore
        );
        onEditCaptable();
      }
      recalcOverridesRef.current.clear();
      cleanAction();
    },
    [
      addReferenceForExistingDocuments,
      saveNotes,
      notes,
      notesHasChanged,
      areCellsValid,
      onEditCaptable,
      captableInfo,
      captableSheet,
      cells,
      cleanAction,
      errorNotification,
      infoNotification,
      getAttributeMapperManager,
      columnsWithLedgers,
      deletedBySecurityTypeChange,
      deletedColumns,
      filesToSave,
      fromNotesToSecurities,
      fromVirtualToReal,
      hiddenColumns,
      isAllocationFinal,
      persistentDeletedColumns,
      saveConfirmation,
      saveProformaColumns,
      selectedMeasurementDate,
      spreadsheetNotes,
      saveSpreadsheetNotes,
      spreadsheetNotesHasChanged,
      updateCapTableData,
      updatedColumns,
      updatedHiddenColumns,
      updatedHiddenNoteColumns,
      updatedTableData,
      setRowGroups,
      visibleColumns,
    ]
  );

  const saveProforma = useCallback(() => {
    // Validation/refresh rejections already notify the user inside saveCapTable.
    saveCapTable(false, true).catch(() => undefined);
  }, [saveCapTable]);

  const { canEditProForma, canCreateProForma } = useProFormaPermissions({
    captableInfo,
    currentFirmRole,
  });

  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const capTableNavItems = useCapTableTertiaryNavItems(firmSlugParam ?? '', companySlugParam ?? '');
  const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState<HTMLElement | null>(null);
  const handleSaveMenuClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => setSaveMenuAnchorEl(e.currentTarget),
    []
  );
  const handleSaveMenuClose = useCallback(() => setSaveMenuAnchorEl(null), []);
  const showSaveMenu = Boolean(onAddRelatedEntity);
  const shouldExposeProformaAction = Boolean(
    captableInfo?.is_primary && (canEditProForma || canCreateProForma || !viewOnlyUser)
  );
  // Keep the version selector's proforma action aligned with the active cap table tab.
  useEffect(() => {
    if (!onSaveAsNewProformaActionChange) {
      return undefined;
    }

    let action: CompanyPageActionHandler;
    if (!captableInfo?.id) {
      action = undefined;
    } else {
      action = shouldExposeProformaAction ? savesAsProformaConfirmation : false;
    }
    onSaveAsNewProformaActionChange(action);

    return () => onSaveAsNewProformaActionChange(undefined);
  }, [captableInfo?.id, onSaveAsNewProformaActionChange, savesAsProformaConfirmation, shouldExposeProformaAction]);
  const saveCapTableRef = useRef(saveCapTable);
  saveCapTableRef.current = saveCapTable;
  const stableSaveCapTable = useCallback(
    () => invokeNavigationSave(() => saveCapTableRef.current(true, false), Boolean(isAllocationFinal)),
    [isAllocationFinal]
  );

  const tertiaryNavState = useMemo(() => {
    if (isEmpty(captableInfo)) {
      return null;
    }
    return {
      ariaLabel: 'Cap Table',
      items: capTableNavItems,
      actions:
        isAllocationFinal !== undefined
          ? [
            {
              type: 'currencySelector' as const,
              currency: isCurrencyLoading ? <CircularProgress size={14} color="inherit" /> : currencyDisplayLabel,
              currencyTooltipTitle: isCurrencyLoading ? undefined : currencyTooltipTitle,
              onCurrencyClick: handleCurrencyMenuOpen,
              disabled: isCurrencyLoading,
              isVisible: !isExportMode,
            },
            {
              type: 'splitButton' as const,
              label: 'Save',
              variant: 'positive' as const,
              disabled: isDisabled,
              isSaving: isCreatingProforma,
              locked: isAllocationFinal,
              menuAriaLabel: 'Save options',
              onClick: stableSaveCapTable,
              ...(showSaveMenu && { onMenuClick: handleSaveMenuClick }),
            },
          ]
          : [],
    };
  }, [
    captableInfo,
    capTableNavItems,
    isCreatingProforma,
    isDisabled,
    isAllocationFinal,
    stableSaveCapTable,
    handleSaveMenuClick,
    showSaveMenu,
    isCurrencyLoading,
    currencyDisplayLabel,
    currencyTooltipTitle,
    handleCurrencyMenuOpen,
    isExportMode,
  ]);
  useTertiaryNavigationRegistration(tertiaryNavState);

  const isGettingInitialData = useMemo(
    () => [captableId, isAllocationFinal, isDisabled].includes(undefined),
    [captableId, isAllocationFinal, isDisabled]
  );
  // Register the existing page action only after the grid and workbook cells are ready.
  useEffect(() => {
    if (!onAddSecurityActionChange) {
      return undefined;
    }

    if (isGettingInitialData || isShowLoadingProgress || !hasCaptableCells) {
      onAddSecurityActionChange(undefined);
      return () => onAddSecurityActionChange(undefined);
    }

    onAddSecurityActionChange(!isDisabled && !isButtonDisabled ? handleAddSecurity : false);

    return () => onAddSecurityActionChange(undefined);
  }, [
    handleAddSecurity,
    hasCaptableCells,
    isButtonDisabled,
    isDisabled,
    isGettingInitialData,
    isShowLoadingProgress,
    onAddSecurityActionChange,
  ]);

  useKeyEventForFabButton(isGettingInitialData);

  useEffect(
    () => () => {
      setIsAllocationFinal(undefined);
      setIsDisabled(false);
    },
    [captableId]
  );

  useEffect(
    () => () => {
      // Unsaved-edit tracking dies with the grid (matches pre-SCAL-8959 semantics:
      // navigating away discards unsaved edits after the unsaved-changes warning).
      useCapTableDirtyCellStore.getState().clearAll();
    },
    []
  );

  // Set the table disabled if the allocation is marked as final
  useEffect(() => {
    if (captableInfo?.id) {
      setCaptableId(Number(captableInfo?.id));
      const verifyAllocationStatus = async () => {
        try {
          const response = (await captableAction.verifyIfHasFinalAllocation(
            Number(captableInfo?.id)
          )) as FinalAllocation[];
          if (response) {
            setFinalAllocations(response);
          }
          setIsAllocationFinal(!isEmpty(response));
          setIsDisabled(!isEmpty(response) || viewOnlyUser);
          setDocumentDisabledStatus(viewOnlyUser);
          // Use the memoized canEditProForma value to avoid flickering
          const shouldDisable = !isEmpty(response) || (viewOnlyUser && !canEditProForma);
          setIsDisabled(shouldDisable);
          setDocumentDisabledStatus(viewOnlyUser && !canEditProForma);
          if (isExportMode) {
            setIsAllocationFinal(true);
            setIsDisabled(true);
          }
          // any as errors are not typed
        } catch (error: any) {
          setIsAllocationFinal(false);
          setIsDisabled(false);
          throw new Error(error);
        }
      };
      verifyAllocationStatus();
    }

    return () => {
      setCaptableId(undefined);
    };
  }, [captableInfo, companyInfo, viewOnlyUser, setDocumentDisabledStatus, isExportMode, canEditProForma]);

  // Open fold groups whose cells demand attention. Load-time reasons (a value
  // still requiring input, an AI suggestion pending review) are derived and
  // fired by useReasonedRowGroupExpands below - re-run per parse, because the
  // first parse can land before AI field metadata hydrates (SCAL-9340). On
  // validation passes, groups owned by securities with invalid cells open.
  // The reasons let these expands through the user-preference guard in
  // useCapTableRowGroups without being persisted.
  const toggleRows = useCallback(() => {
    // The workbook keys sheets by their configured name - 'captable' (see
    // useCaptableData), NOT 'capTable'; the wrong key scanned nothing at all.
    const capCells = cells.captable as unknown as Record<string, CapTableCell>;
    const reasonByGroup: Record<string, SystemExpandReason> = {};

    each(capCells, cell => {
      if (cell.isValid !== false) {
        return;
      }
      const securityTypeKey = cell.columnLegend! + SECURITY_TYPE_ROW_NUMBER;
      const securityType = toString(capCells[securityTypeKey] ? capCells[securityTypeKey].value : '0');
      if (securityType === PREFERRED_STOCK.toString() || securityType === WARRANT.toString()) {
        reasonByGroup[PREFERRED_TERMS_ALIAS] = 'invalid-save';
      }
      if (securityType === OPTION.toString() || securityType === WARRANT.toString()) {
        reasonByGroup[STRIKE_PRICE_ALIAS] = 'invalid-save';
      }
    });

    Object.entries(reasonByGroup).forEach(([row, reason]) => {
      setRowGroups({ type: SYSTEM_EXPAND, row, reason });
    });
  }, [cells, setRowGroups]);

  useReasonedRowGroupExpands(cells?.captable as unknown as Record<string, CapTableCell> | undefined, setRowGroups);

  // When the PDF dialog is open or the page is rendered for PDF capture, every
  // collapsible section must be visible so nothing stays hidden behind a "+".
  // This overrides the fold state at render time (without mutating or persisting
  // the user's saved preferences) so the live table returns to the user's
  // collapse state once the dialog closes.
  const isPdfContext = openPdfDialog || isExportMode;
  const effectiveRowGroups = useMemo(() => {
    if (!isPdfContext) {
      return rowGroups;
    }
    const expanded: Record<string, string> = { ...(rowGroups as Record<string, string>) };
    // Flip any currently collapsed group to expanded.
    Object.keys(expanded).forEach(key => {
      if (expanded[key] === SYSTEM_COLLAPSE || expanded[key] === USER_COLLAPSE) {
        expanded[key] = SYSTEM_EXPAND;
      }
    });
    // Ensure the known collapsible groups are expanded even before their fold
    // state has been registered (firm rows are added once fund data loads).
    [
      PREFERRED_TERMS_ALIAS,
      STRIKE_PRICE_ALIAS,
      FIRM_SHARES_ALIAS,
      FIRM_OWNERSHIP_PERCENTAGE_ALIAS,
      FIRM_LIQUIDATION_PREFERENCE_ALIAS,
    ].forEach(row => {
      expanded[row] = SYSTEM_EXPAND;
    });
    return expanded;
  }, [isPdfContext, rowGroups]);

  const getLinkedCellUpdates = useCallback(
    newCells => {
      // Get updates for linked cells
      const cellUpdates = linkedCellsUpdates({
        prevCells: cells as unknown as Record<string, CapTableCell>,
        cells: newCells,
      });

      return cellUpdates;
    },
    [cells]
  );

  const onTableChange = useCallback(
    (cell: CapTableCellWithReferences, value: CellValue) => {
      onChange(cell, value, () => {});
    },
    [onChange]
  );

  const capTableContextValue = useMemo(
    () => ({
      measurementDate,
      selectedMeasurementDate,
      rowGroups: effectiveRowGroups,
      setRowGroups,
      isDisabled: false,
      cells,
      getLinkedCellUpdates,
      convertibleNotesFieldAttrs,
      hasVariableLiquidationPreference: hasVariableLiquidationPreferenceLedger,
      setHasVariableLiquidationPreference: setHasVariableLiquidationPreferenceLedger,
      hasVariableDividendRate,
      setHasVariableDividendRate,
      divRateLedgerItemAttrs,
    }),
    [
      cells,
      hasVariableLiquidationPreferenceLedger,
      setHasVariableLiquidationPreferenceLedger,
      hasVariableDividendRate,
      setHasVariableDividendRate,
      convertibleNotesFieldAttrs,
      getLinkedCellUpdates,
      measurementDate,
      effectiveRowGroups,
      selectedMeasurementDate,
      divRateLedgerItemAttrs,
      setRowGroups,
    ]
  );

  const renderProformaCaptable = useMemo(
    () => (
      <ProformaCaptableDialog
        editMode={false}
        initialValues={initialFormValues}
        isValid={(formState?.isValid || false) && (!nrmState?.isEnabled || (nrmState?.isValid ?? false))}
        onClose={() => {
          setOpenProformaDialog(false);
          setNrmState(null);
        }}
        onFormChange={updatedFormState => setFormState(updatedFormState)}
        onSave={saveProforma}
        open={openProformaDialog || false}
        tableTerms={tableTerms}
        title={`New Proforma ${tableTerms.tableName}`}
        captableInfo={captableInfo}
        fundList={fundList ?? []}
        onNewRoundModelerChange={setNrmState}
        customButtonLabel={nrmState?.isEnabled ? 'Create Proforma Cap Table and Fund Ownership' : undefined}
      />
    ),
    [captableInfo, formState, fundList, initialFormValues, nrmState, openProformaDialog, saveProforma, tableTerms]
  );

  const capTableReady = Boolean(data && cells && captableSheet?.data);
  let capTableExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!capTableReady || isShowLoadingProgress) {
    capTableExportStatus = 'loading';
  } else if (showEmptyTableMessage) {
    capTableExportStatus = 'missing';
  } else if (isEmpty(visibleColumns)) {
    // Emptiness is not settled yet: `showEmptyTableMessage` only flips after DEBOUNCE_DELAY,
    // and reporting `ready` during that window lets capture stamp a header-only blank page.
    // Hold on `loading` until the debounce resolves it to `missing` — never `ready` over a
    // grid with no columns.
    capTableExportStatus = 'loading';
  }
  useExportSignal({
    target: PdfExportTargets.CAP_TABLE.id,
    status: capTableExportStatus,
    enabled: isExportMode,
  });

  if (!captableSheet) {
    return (
      <Box data-ref={PdfExportTargets.CAP_TABLE.id} sx={isExportMode ? EXPORT_MODE_STYLES : undefined}>
        <GridSkeleton />
      </Box>
    );
  }

  const isDataReady = !!(data && cells && captableSheet?.data);

  const readyGrid = isDataReady ? (
    <>
      {/* Save options menu for the TertiaryNavigation split button */}
      <PrimaryNavigationMenu
        anchorEl={saveMenuAnchorEl}
        open={Boolean(saveMenuAnchorEl)}
        menuMinWidth={0}
        menuWidth="content"
        zoomLevel={zoomLevel}
        onClose={handleSaveMenuClose}>
        <PrimaryNavigationMenuItem
          icon={<AddBusinessOutlinedIcon fontSize="inherit" />}
          onClick={() => {
            handleSaveMenuClose();
            onAddRelatedEntity?.();
          }}>
          {RELATED_ENTITY.ADD_ACTION}
        </PrimaryNavigationMenuItem>
      </PrimaryNavigationMenu>
      {/* Proforma Cap Table */}
      {renderProformaCaptable}

      {isDisabled && isAllocationFinal && (
        <AllocationFinalMessage
          tableTerms={tableTerms}
          finalAllocations={finalAllocations}
          setIsDisabled={setIsDisabled}
          DocumentsDisabled={setDocumentDisabledStatus}
          savesAsProformaConfirmation={savesAsProformaConfirmation}
          variant
          actions={[
            {
              label: 'Duplicate',
              buttonProps: {
                variant: 'contained',
                size: 'small',
                disabled: viewOnlyUser,
              },
              callback: savesAsProformaConfirmation,
            },
          ]}
          viewOnlyUser={viewOnlyUser}
        />
      )}

      {isAllocationFinal && !isDisabled && <Alert isAlertVisible>{CAP_TABLE_LOCKED_MESSAGE}</Alert>}

      <WorkbookContext.Provider value={workbookContextValue}>
        <Box
          ref={captableContainerRef}
          data-ref={PdfExportTargets.CAP_TABLE.id}
          sx={{
            position: 'relative',
            paddingTop: showAiNameBanner && !isDisabled ? '28px' : 0,
            // SCAL-8976 (Feature A): the page already reserves the bottom-drawer height via
            // CompanyContainer's <DrawerBottomSpacer>, so no margin is needed here (it would
            // double the reservation). Drawer occlusion of the scrolled review cell is handled
            // by scroll-margin-bottom on .cell-review-selected (see _cell.scss).
            ...(isExportMode ? EXPORT_MODE_STYLES : {}),
          }}>
          {/* SCAL-8976: mirrors hovered/active review cells onto grid cell DOM nodes
              (class toggle, no re-render). Resolves the GRID's document so it works
              when the review panel is popped out into another window. */}
          {!isExportMode && <ReviewCellOutline gridRef={captableContainerRef} />}
          {hasPendingAINameFindings && !isDisabled && (
            <AISuggestionsColumnBanner
              cells={workbook?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined}
              containerRef={captableContainerRef}
            />
          )}
          <ScalarSpreadsheet
            {...captableSheet}
            {...restCaptableProps}
            {...sharedProps}
            cloneColumn={cloneColumn}
            allowCloneColumn={!isDisabled}
            setColumns={handleSpreadsheetColumnsChange}
            rowGroups={effectiveRowGroups}
            setRowGroups={setRowGroups}
            toggleRows={toggleRows}
            deleteColumn={showDeleteConfirmation}
            tableTerms={tableTerms}
            sheet={captableSheet}
            format={format}
            formatDispatch={formatDispatch}
            addMultipleColumns={!isDisabled ? addSecurity : undefined}
            allowDeleteColumn={!isDisabled}>
            {/* any due to legacy component typing */}
            {showEmptyTableMessage ? ((<EmptyTableMessage tableTerms={tableTerms} />) as any) : null}
          </ScalarSpreadsheet>
        </Box>
      </WorkbookContext.Provider>
    </>
  ) : null;

  if (readyGrid && captableInfo?.id) {
    lastReadyGridRef.current = { id: captableInfo.id, element: readyGrid };
  }
  // While the workbook re-derives in place (the store still holds the same loaded
  // cap table), keep the last ready grid mounted instead of flashing the skeleton.
  // The skeleton is reserved for first loads and full reloads, when the store blanks.
  const lastReadyGrid = lastReadyGridRef.current;
  const gridContent =
    readyGrid ?? (lastReadyGrid && lastReadyGrid.id === captableInfo?.id ? lastReadyGrid.element : null);

  return (
    <CitationProvider>
      <AIFieldAcceptProvider onDismissAiColumn={handleDismissAiColumn}>
        <CapTableContext.Provider value={capTableContextValue as unknown as CapTableContextType}>
          {gridContent ?? (
            <Box data-ref={PdfExportTargets.CAP_TABLE.id} sx={isExportMode ? EXPORT_MODE_STYLES : undefined}>
              <GridSkeleton />
            </Box>
          )}

          <CapTableWidgetDrawer
            workbook={workbook}
            onTableChange={onTableChange}
            captableSetProgrammaticUpdate={setCaptableSetProgrammaticUpdate}
            applicationContext={widgetDrawerProps.applicationContext}
            notesProps={widgetDrawerProps.notesProps}
            documentReferencesProps={widgetDrawerProps.documentReferencesProps}
            spreadsheetNotesProps={widgetDrawerProps.spreadsheetNotesProps}
          />

          <ExtendedFabButton
            addBottomMarginForDrawer
            id="captable-add-security-btn"
            disabled={isShowLoadingProgress || isDisabled || isButtonDisabled || !hasCaptableCells}
            label="Add Security"
            onClick={handleAddSecurity}
          />
          <CurrencyMenu
            data-testid="captable-currency-menu"
            anchorEl={currencyMenuAnchorEl}
            currencies={currencies}
            isCurrencyLoading={isCurrencyLoading}
            onClose={handleCurrencyMenuClose}
            onCurrencyChange={handleCurrencyChange}
            showMore={showMore}
            showLess={showLess}
            usedCurrencies={usedCurrencies}
          />
        </CapTableContext.Provider>
      </AIFieldAcceptProvider>
    </CitationProvider>
  );
};

export default CapTableTable;
